/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { FormControlLabel, Switch } from "@mui/material";
import { useComposerMode } from "cms/components/hooks/use-composer-mode";
import { useCurrentDynamicField } from "cms/fields/hooks/use-current-dynamic-field";
import { useSelectFieldSchema } from "cms/fields/hooks/use-select-field-schema";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Logger } from "cms/utils/log/logger";
import { useFieldZone } from "cms/fields/hooks/use-field-zone";

const FieldDefaultsButton = () => {
    
    const { t } = useTranslation();
    const { contentOnly } = useComposerMode();
    
    const { field } = useCurrentDynamicField();
    const name = field?.name;
    // const settings = field?.settings;
    const { FieldZoneState, applyFieldChanges } = useFieldZone();
    const wasAlreadyDefault = FieldZoneState?.wasAlreadyDefault ?? false;
    const canUseDefaults = FieldZoneState ?? false;
    
    const schema = useSelectFieldSchema(name);
    const values = schema?.values;
    const schemaSettings = schema?.settings;
    const noOverride = schemaSettings?.noOverride ?? false;
    const defaultStyle = schemaSettings?.defaultStyle;
    const defaultValue = schemaSettings?.defaultValue;
    const defaultFormat = schemaSettings?.defaultFormat;
    
    const disabled = noOverride || wasAlreadyDefault;
    const [defaultEnabled, setDefaultEnabled] = useState(disabled);
    
    // TODO: Disabled defaults - do not delete
    const handleUseDefaultToggle = () => {
        Logger.debug(`Called handleUseDefaultToggle '${defaultEnabled}' '${defaultValue}' '${defaultStyle}'`);
        setDefaultEnabled(!defaultEnabled);
        if (!defaultEnabled) {
            const updatedValues = [];
            for (const item of values) {
                let value = item?.value;
                let settings = item?.settings;
                Logger.debug(`handleUseDefaultToggle '${defaultEnabled}' '${defaultValue}' '${defaultStyle}' ${value}`);
                let updatedValue = { ...item };
                let updatedSettings = { ...settings };
                if (defaultStyle) {
                    updatedSettings.style = defaultStyle;
                }
                if (defaultFormat) {
                    updatedSettings.format = defaultFormat;
                }
                updatedValue.value = defaultValue;
                updatedValue.settings = updatedSettings;
                updatedValues.push(updatedValue);
            }
            const newState = {
                ...field,
                values: updatedValues,
            };
            
            applyFieldChanges(field, newState);
            
            // Note: This changes multiple settings at once
            //    ...individual settings are now done differently
            //    updateFieldSetting('hide', value)
            
        }
    };
    
    // TODO: Disabled defaults - do not delete -
    //    but also this part needs to change to not watch localState for changes causing too many updates
    // useEffect(() => {
    //     return debouncedAction(() => {
    //         let updatedField = {
    //             ...field,
    //             ...localState,
    //         }
    //         const {original, modified} = prepareChangeData(field, updatedField)
    //         const isDirty = checkDiffs(original, modified, 'applyFieldChanges')
    //         if (isDirty) {
    //             Logger.debug('%c ComponentFieldEdit - localState effect detected (useEffect)', 'color: #bffff9')
    //             applyFieldChanges(original, modified)
    //         }
    //         // applyFieldChanges(field, localState, controlledProperties)
    //     })
    // }, [localState])
    
    return (
        <Fragment>
            {!contentOnly && (
                <>
                    {/*  if the "value" or "style" or "format" are changed from the defaults... */}
                    {/*  - then this toggle must be off  */}
                    {/*  This toggle then works like a reset button to restore the default settings when turned to on*/}
                    {/*  The value of this toggle should not be set to anything in settings  */}
                    {/*     onChange (ON)   */}
                    {/*       - should set the field "Value" using the schema provided default value   */}
                    {/*       - should set the field "Style" using the schema provided default style   */}
                    {/*       - should set the field "Format" using the schema provided default format   */}
                    {/*     onChange (OFF) should do nothing   */}
                    {/* when noOverride is true - this toggle should not be shown */}

                    {/* TODO: Disabled defaults - do not delete */}

                    {canUseDefaults && (
                        <>
                            {!noOverride &&
                                <FormControlLabel
                                    value={`${name}_required`}
                                    control={
                                        <Switch
                                            name={`${name}_required`}
                                            size='small'
                                            checked={defaultEnabled}
                                        />
                                    }
                                    label={t("commons.useDefaultDisplayValueSettings")}
                                    labelPlacement='end'
                                    disabled={noOverride}
                                    onChange={handleUseDefaultToggle}
                                    style={{ margin: "4px", marginBottom: 0 }}
                                />
                            }
                        </>
                    )}
                    
                </>
            )}
        </Fragment>
    );
};

export { FieldDefaultsButton };
